

import type * as axios from 'axios';
import Bugsnag from '@bugsnag/js';

import type { ApiResponseErrors} from './ApiResponseErrorInterfaces';
import { ErrorStatusEnum, ErrorCodeEnum } from './ApiResponseErrorInterfaces';

export interface ValidationErrors {
  [key: string]: string[];
}

export class ValidationError extends Error {
  constructor(
    public override readonly message: string,
    public readonly code: string,
    public readonly errors: ValidationErrors,
  ) {
    super();
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export class ErrorResponse extends Error {
  constructor(public override readonly message: string, public readonly code: string) {
    super();
    Object.setPrototypeOf(this, ErrorResponse.prototype);
  }
}

export class RequestCancel extends Error {
  constructor(public override readonly message: string, public readonly code: string) {
    super();
    Object.setPrototypeOf(this, RequestCancel.prototype);
  }
}

export type ServerErrors = ValidationError | ErrorResponse;

const getErrorCode = (error: axios.AxiosError<ApiResponseErrors>): ErrorStatusEnum | number => {
  if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
    return ErrorStatusEnum.canceled;
  }

  if (error?.response?.status === 500) {
    return 500;
  }

  return ErrorStatusEnum.http;
};

/**
 * To format the errors from the connection or server.
 * It's like 401, 500, Network Error, etc.
 * @param error
 */

export const getError = (
  error: axios.AxiosError<ApiResponseErrors> | axios.AxiosError<never>,
): ServerErrors => {
  if (error.code === 'ERR_CANCELED' || error.message === 'canceled') {
    return new RequestCancel('Request canceled', 'cancel');
  }

  const axiosResponse = error?.response;
  if (
    axiosResponse
    && axiosResponse.data
    && typeof axiosResponse.data === 'object'
    && 'error' in axiosResponse.data
  ) {
    if (axiosResponse.data.error.error_code === ErrorCodeEnum.validation) {
      return new ValidationError(
        axiosResponse.data.error.error_message,
        axiosResponse.data.error.error_code,
        axiosResponse.data.error.errors,
      );
    }
    return new ErrorResponse(
      axiosResponse.data.error.error_message,
      axiosResponse.data.error.error_code,
    );
  }

  Bugsnag.notify(error);

  console.log(error);
  const errorCode = getErrorCode(error);
  return new ErrorResponse(
    'The application has encountered an unknown error. Please call (866) 985-7598 for assistance.',
    `${errorCode}`,
  );
};

export interface ApiResponse<Response = unknown> {
  data: Response;
}
